/**
 * Auth store using Zustand — manages authentication state globally.
 */

import { create } from 'zustand';
import { api } from '../api/client';
import type { AuthUser } from '@shared/types';

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  firstPipelineId: string | null;

  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  googleLogin: (credential: string) => Promise<{ isNewUser: boolean }>;
  logout: () => Promise<void>;
  loadUser: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  firstPipelineId: null,

  login: async (email: string, password: string) => {
    const { data } = await api.post('/auth/login', { email, password });
    const { user, token, refreshToken, firstPipelineId } = data.data;
    localStorage.setItem('sf_token', token);
    localStorage.setItem('sf_refresh_token', refreshToken);
    set({ user, isAuthenticated: true, firstPipelineId: firstPipelineId || null });
  },

  register: async (email: string, password: string, name: string) => {
    const { data } = await api.post('/auth/register', { email, password, name });
    const { user, token, refreshToken, firstPipelineId } = data.data;
    localStorage.setItem('sf_token', token);
    localStorage.setItem('sf_refresh_token', refreshToken);
    set({ user, isAuthenticated: true, firstPipelineId: firstPipelineId || null });
  },

  googleLogin: async (credential: string) => {
    const { data } = await api.post('/auth/google', { credential });
    const { user, token, refreshToken, firstPipelineId, isNewUser } = data.data;
    localStorage.setItem('sf_token', token);
    localStorage.setItem('sf_refresh_token', refreshToken);
    set({ user, isAuthenticated: true, firstPipelineId: firstPipelineId || null });
    return { isNewUser };
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore logout errors
    } finally {
      localStorage.removeItem('sf_token');
      localStorage.removeItem('sf_refresh_token');
      set({ user: null, isAuthenticated: false, firstPipelineId: null });
    }
  },

  loadUser: async () => {
    const token = localStorage.getItem('sf_token');
    if (!token) {
      set({ isLoading: false });
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      set({ user: data.data.user, isAuthenticated: true, isLoading: false });
    } catch {
      localStorage.removeItem('sf_token');
      localStorage.removeItem('sf_refresh_token');
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },
}));
